import React, { useState, useEffect } from 'react';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { generateDocument, getDocument, showPreClearance } from '@app/api/pre-clearance.api';
import { notification } from 'antd';
import { PreClearanceDocumentRequest } from '@app/models/pre_clearance/PreClearanceRequest';
import { useNavigate } from 'react-router-dom';

interface GenerateDocumentsButtonProps {
  id: number;
  hasLetter: boolean;
  hasCertificate: boolean;
  hasAnnex: boolean;
  onRefresh?: () => Promise<void>;
}

const GenerateDocumentsButton: React.FC<GenerateDocumentsButtonProps> = ({
  id,
  hasLetter,
  hasCertificate,
  hasAnnex,
  onRefresh,
}) => {
  const [generatedDocuments, setGeneratedDocuments] = useState<{ [key: string]: boolean }>({
    letter: false,
    certificate: false,
    annex: false,
  });

  const [preClearanceDocs, setPreClearanceDocs] = useState<{ [key: string]: boolean }>({
    letter: false,
    certificate: false,
    annex: false,
  });

  const navigate = useNavigate();

  // fetch pre_clearance to check if documents already exist

  const fetchPreClearanceDocs = async () => {
    try {
      const response = await showPreClearance(id);
      const pc = response?.pre_clearance;

      if (!pc) return;

      const docs: { [key: string]: boolean } = {
        letter: false,
        certificate: false,
        annex: false,
      };

      if (pc.pre_clearance_files) {
        const filesArray = Array.isArray(pc.pre_clearance_files) ? pc.pre_clearance_files : [pc.pre_clearance_files];

        filesArray.forEach((file: { type?: string }) => {
          const type = file.type?.toLowerCase();

          if (type === 'letter') docs.letter = true;
          if (type === 'certificate') docs.certificate = true;
          if (type === 'extension') docs.annex = true;
        });
      }

      // Legacy support
      if (pc.letter_name) docs.letter = true;
      if (pc.certificate_name) docs.certificate = true;
      if (pc.extension_name) docs.annex = true;

      setPreClearanceDocs(docs);

      // Optional: clear generated state since document now exists in backend
      setGeneratedDocuments({
        letter: false,
        certificate: false,
        annex: false,
      });
    } catch (err) {
      console.error('Error fetching pre_clearance:', err);
    }
  };

  useEffect(() => {
    if (id) {
      fetchPreClearanceDocs();
    }
  }, [id]);

  const handleGenerateDocument = async (documentType: string) => {
    if (!id) {
      notification.error({
        message: 'Error',
        description: 'ID is required to generate the document.',
      });
      return;
    }

    try {
      const documentData: PreClearanceDocumentRequest = {
        type: documentType.toUpperCase() as 'LETTER' | 'CERTIFICATE' | 'EXTENSION',
        doc_url: undefined,
        message: '',
      };

      const result = await generateDocument(id, documentData);

      notification.success({
        message: `Successfully generated ${documentType}`,
        description: result?.message ?? 'Document generated successfully!',
      });
      await onRefresh?.();
      // navigate(`/pre-clearance`);
    } catch (error: any) {
      notification.error({
        message: `Failed to generate ${documentType}`,
        description: error.message ?? 'An error occurred while generating the document.',
      });
    }
  };

  const handleViewGeneratedDocument = async (documentType: string) => {
    if (!id) {
      notification.error({
        message: 'Error',
        description: 'Invalid document details.',
      });
      return;
    }

    try {
      const documentData: PreClearanceDocumentRequest = {
        type: documentType.toUpperCase() as 'LETTER' | 'CERTIFICATE' | 'EXTENSION',
        doc_url: undefined,
        message: '',
      };

      const result = await getDocument(id, documentData);

      if (result?.doc_url) {
        window.open(result.doc_url, '_blank');
        notification.success({
          message: `Viewing ${documentType}`,
          description: 'You are now viewing the generated document.',
        });
      } else {
        notification.error({
          message: 'Document not found',
          description: 'The requested document was not found.',
        });
      }
    } catch (error: any) {
      notification.error({
        message: `Failed to view ${documentType}`,
        description: error.message ?? 'An error occurred while retrieving the document.',
      });
    }
  };

  return (
    <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
      {hasLetter &&
        !preClearanceDocs.letter &&
        (generatedDocuments.letter ? (
          <BaseButton
            type="primary"
            onClick={() => handleViewGeneratedDocument('letter')}
            style={{ height: '30px', padding: '0 15px' }}
          >
            View Generated Letter
          </BaseButton>
        ) : (
          <BaseButton
            type="primary"
            onClick={() => handleGenerateDocument('letter')}
            style={{ height: '30px', padding: '0 15px' }}
          >
            Generate Letter
          </BaseButton>
        ))}

      {hasCertificate &&
        !preClearanceDocs.certificate &&
        (generatedDocuments.certificate ? (
          <BaseButton
            type="primary"
            onClick={() => handleViewGeneratedDocument('certificate')}
            style={{ height: '30px', padding: '0 15px' }}
          >
            View Generated Certificate
          </BaseButton>
        ) : (
          <BaseButton
            type="primary"
            onClick={() => handleGenerateDocument('certificate')}
            style={{ height: '30px', padding: '0 15px' }}
          >
            Generate Certificate
          </BaseButton>
        ))}

      {hasAnnex &&
        !preClearanceDocs.annex &&
        (generatedDocuments.annex ? (
          <BaseButton
            type="primary"
            onClick={() => handleViewGeneratedDocument('annex')}
            style={{ height: '30px', padding: '0 15px' }}
          >
            View Generated Annex
          </BaseButton>
        ) : (
          <BaseButton
            type="primary"
            onClick={() => handleGenerateDocument('annex')}
            style={{ height: '30px', padding: '0 15px' }}
          >
            Generate Annex
          </BaseButton>
        ))}
    </div>
  );
};

export default GenerateDocumentsButton;
